import { useCallback, useMemo, useRef, useState } from "react";

export interface SectionCollapseApi {
  value: string[];
  setValue: (next: string[]) => void;
  openAll: () => void;
  closeAll: () => void;
  forceOpen: (id: string) => void;
  toggle: (id: string) => void;
}

const defaultOpen = (sectionIds: readonly string[]): string[] =>
  sectionIds[0] ? [sectionIds[0]] : [];

export function useSectionCollapse(
  canvasKey: string,
  sectionIds: readonly string[],
): SectionCollapseApi {
  const [explicit, setExplicit] = useState<string[] | null>(null);

  const prevKeyRef = useRef(canvasKey);
  if (prevKeyRef.current !== canvasKey) {
    prevKeyRef.current = canvasKey;
    setExplicit(null);
  }

  const value = useMemo(
    () => (explicit !== null ? explicit : defaultOpen(sectionIds)),
    [explicit, sectionIds],
  );

  const setValue = useCallback((next: string[]) => setExplicit(next), []);

  const openAll = useCallback(
    () => setExplicit(Array.from(sectionIds)),
    [sectionIds],
  );

  const closeAll = useCallback(() => setExplicit([]), []);

  const forceOpen = useCallback(
    (id: string) =>
      setExplicit((prev) => {
        const base = prev ?? defaultOpen(sectionIds);
        return base.includes(id) ? base : [...base, id];
      }),
    [sectionIds],
  );

  const toggle = useCallback(
    (id: string) =>
      setExplicit((prev) => {
        const base = prev ?? defaultOpen(sectionIds);
        return base.includes(id) ? base.filter((x) => x !== id) : [...base, id];
      }),
    [sectionIds],
  );

  return { value, setValue, openAll, closeAll, forceOpen, toggle };
}
